import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar from './Navbar';

// Compute SHA-256 entirely in browser
async function computeSHA256(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function ProofPage() {
  const { proofId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const [photoChecking, setPhotoChecking] = useState(false);
  const [computedHash, setComputedHash] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProof = async () => {
      try {
        const docRef = doc(db, 'proof_records', proofId.toUpperCase());
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setRecord(docSnap.data());
        } else {
          setNotFound(true);
        }
      } catch (e) {
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchProof();
  }, [proofId]);

  const handlePhotoSelect = async (file) => {
    if (!file || !record) return;
    setPhotoChecking(true);
    setPhotoResult(null);
    setComputedHash('');
    try {
      const hash = await computeSHA256(file);
      setComputedHash(hash);
      setPhotoResult(hash === record.fileHash ? 'match' : 'mismatch');
    } catch(e) {
      setPhotoResult('error');
    }
    setPhotoChecking(false);
  };

  const copyProofId = () => {
    navigator.clipboard.writeText(proofId.toUpperCase());
    alert('Proof ID copied!');
  };

  const shareProof = () => {
    if (navigator.share) {
      navigator.share({
        title: 'SafeSnap Verified Proof',
        text: `Verify this SafeSnap proof: ${proofId}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="spinner" style={{ marginTop: 80 }} />
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Navbar />
        <div className="container" style={{ maxWidth: 600, padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>❌</div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 800, marginBottom: 12 }}>
            Proof not found
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 16, lineHeight: 1.7, marginBottom: 28 }}>
            No record found for <strong style={{ fontFamily: 'var(--mono)' }}>{proofId}</strong>. This proof may not exist or the photo was not taken with SafeSnap.
          </p>
          <button className="btn-primary" onClick={() => navigate('/verify')}>
            Try another Proof ID
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container" style={{ maxWidth: 720, padding: '48px 24px' }}>

        {/* Verified banner — Layer 1: Capture Record */}
        <div style={{ background: 'rgba(0,200,138,0.08)', border: '1.5px solid rgba(0,200,138,0.25)', borderRadius: 16, padding: '20px 24px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, background: 'rgba(0,200,138,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 22 }}>✅</span>
          </div>
          <div>
            <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink)', marginBottom: 4 }}>
              Layer 1 — Capture Record: Authentic
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.5 }}>
              A photo was captured with SafeSnap on this date at this location. The original capture record is on file.
            </p>
          </div>
        </div>

        {/* Layer 2 notice */}
        <div style={{ background: 'rgba(26,60,255,0.06)', border: '1px solid rgba(26,60,255,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 18 }}>📱</span>
          <div>
            <p style={{ fontWeight: 700, fontSize: 12, color: '#1A3CFF', marginBottom: 2 }}>Layer 2 — File Integrity: Verify in app</p>
            <p style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
              To confirm the photo file has not been edited since capture, open SafeSnap app → Verify → select the photo. Full verification requires both layers.
            </p>
          </div>
        </div>

        {/* Proof card */}
        <div className="card-dark" style={{ marginBottom: 20 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', marginBottom: 6 }}>PROOF ID</div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
                {record.proofId}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={copyProofId} style={{ background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                Copy ID
              </button>
              <button onClick={shareProof} style={{ background: 'var(--accent)', border: 'none', color: 'white', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
                Share
              </button>
            </div>
          </div>

          {/* Use case badge */}
          <div style={{ marginBottom: 16 }}>
            <span style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--mono)', fontSize: 10, padding: '4px 12px', borderRadius: 100, letterSpacing: '0.06em' }}>
              {record.useCase?.toUpperCase()}
            </span>
          </div>

          {/* Meta grid */}
          <div className="meta-grid">
            {[
              { key: 'TIMESTAMP', val: record.timestamp },
              { key: 'GPS LATITUDE', val: record.latitude },
              { key: 'GPS LONGITUDE', val: record.longitude },
              { key: 'DEVICE', val: record.device },
              { key: 'IMAGE SIZE', val: `${record.imageWidth} × ${record.imageHeight} px` },
              { key: 'FILE SIZE', val: record.fileSize ? `${Math.round(record.fileSize / 1024)} KB` : 'N/A' },
            ].map((m, i) => (
              <div key={i} className="meta-cell">
                <div className="meta-key">{m.key}</div>
                <div className="meta-val">{m.val || 'N/A'}</div>
              </div>
            ))}
          </div>

          {/* Hash */}
          <div style={{ marginTop: 16, background: 'rgba(26,60,255,0.12)', border: '1px solid rgba(26,60,255,0.2)', borderRadius: 10, padding: '14px' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(100,120,255,0.7)', letterSpacing: '0.08em', marginBottom: 8 }}>SHA-256 CRYPTOGRAPHIC FINGERPRINT</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'rgba(255,255,255,0.5)', wordBreak: 'break-all', lineHeight: 1.6 }}>{record.fileHash}</div>
          </div>

          {/* Integrity */}
          <div style={{ marginTop: 12, background: 'rgba(0,200,138,0.1)', border: '1px solid rgba(0,200,138,0.2)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--green)' }}>✓</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)' }}>
              Cryptographic integrity confirmed — metadata cannot be altered post-capture
            </span>
          </div>
        </div>

        {/* STEP 2 — Photo verification */}
        <div style={{ background: 'white', border: '0.5px solid rgba(10,10,15,0.1)', borderRadius: 16, padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: photoResult === 'match' ? '#00C88A' : photoResult === 'mismatch' ? '#FF4D2E' : '#1A3CFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>{photoResult === 'match' ? '✓' : photoResult === 'mismatch' ? '✗' : '2'}</span>
            </div>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Verify the photo is not edited</h3>
              <p style={{ fontSize: 12, color: 'var(--muted)' }}>Upload the photo to confirm it matches the original — computed in your browser, never uploaded</p>
            </div>
          </div>

          <div
            onDrop={e => { e.preventDefault(); handlePhotoSelect(e.dataTransfer.files[0]); }}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${photoResult === 'match' ? '#00C88A' : photoResult === 'mismatch' ? '#FF4D2E' : 'rgba(10,10,15,0.15)'}`,
              borderRadius: 12, padding: '24px 16px', textAlign: 'center', cursor: 'pointer',
              background: photoResult === 'match' ? 'rgba(0,200,138,0.04)' : photoResult === 'mismatch' ? 'rgba(255,77,46,0.04)' : 'var(--surface)',
            }}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handlePhotoSelect(e.target.files[0])} />
            {photoChecking ? (
              <p style={{ fontSize: 13, color: 'var(--muted)' }}>Computing fingerprint in your browser...</p>
            ) : photoResult === 'match' ? (
              <div>
                <p style={{ fontSize: 36, marginBottom: 8 }}>✅</p>
                <p style={{ fontWeight: 700, fontSize: 15, color: '#00C88A', marginBottom: 4 }}>Capture Record: Authentic</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>Byte-for-byte identical to the original capture</p>
              </div>
            ) : photoResult === 'mismatch' ? (
              <div>
                <p style={{ fontSize: 36, marginBottom: 8 }}>❌</p>
                <p style={{ fontWeight: 700, fontSize: 15, color: '#FF4D2E', marginBottom: 4 }}>Photo does not match</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>This photo differs from the original — it may have been edited or re-saved</p>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 32, marginBottom: 8 }}>📸</p>
                <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', marginBottom: 4 }}>Drop the photo here or tap to select</p>
                <p style={{ fontSize: 12, color: 'var(--muted)' }}>Photo never leaves your device</p>
              </div>
            )}
          </div>

          {computedHash && (
            <div style={{ marginTop: 12, background: 'var(--surface)', borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 4 }}>ORIGINAL FINGERPRINT</p>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: photoResult === 'match' ? '#00C88A' : '#FF4D2E', wordBreak: 'break-all', lineHeight: 1.5, marginBottom: 10 }}>{record.fileHash}</p>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 4 }}>YOUR PHOTO FINGERPRINT</p>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: photoResult === 'match' ? '#00C88A' : '#FF4D2E', wordBreak: 'break-all', lineHeight: 1.5 }}>{computedHash}</p>
            </div>
          )}
        </div>

        {/* Final verdict */}
        {photoResult && (
          <div style={{
            background: photoResult === 'match' ? 'rgba(0,200,138,0.08)' : 'rgba(255,77,46,0.08)',
            border: `2px solid ${photoResult === 'match' ? 'rgba(0,200,138,0.3)' : 'rgba(255,77,46,0.3)'}`,
            borderRadius: 16, padding: 24, textAlign: 'center', marginBottom: 20,
          }}>
            <p style={{ fontSize: 40, marginBottom: 10 }}>{photoResult === 'match' ? '✅' : '❌'}</p>
            <h2 style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 800, color: 'var(--ink)', marginBottom: 8 }}>
              {photoResult === 'match' ? '100% Verified — Fully Authentic' : 'Verification Failed'}
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
              {photoResult === 'match'
                ? 'This capture record is authentic. To verify the photo file has not been edited, open SafeSnap app → Verify → select the photo.'
                : 'The Proof ID exists but this photo does not match the original. It may have been edited or replaced after capture.'}
            </p>
          </div>
        )}

        {/* Location Trust — Always Show, Visually Communicate Trust */}
        {record.latitude && record.longitude && (
          <div style={{ background: 'white', border: '0.5px solid rgba(10,10,15,0.1)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 12 }}>Capture Location</h3>

            {/* Trust banner */}
            {(() => {
              const trust = record.locationTrust || 'UNVERIFIABLE';
              const network = record.networkTypeAtCapture || 'NONE';
              const isHighWifi = trust === 'HIGH' && network === 'WIFI';
              const isHighCell = trust === 'HIGH' && network === 'CELLULAR';
              const isMedium = trust === 'MEDIUM';
              const isLow = trust === 'LOW';
              const isUnverifiable = trust === 'UNVERIFIABLE';

              return (
                <>
                  {/* Banner */}
                  {isMedium && (
                    <div style={{ background: 'rgba(200,168,75,0.1)', border: '1px solid rgba(200,168,75,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 12, color: '#856404' }}>Location unconfirmed</p>
                        <p style={{ fontSize: 11, color: '#856404' }}>{record.locationTrustNote || 'Minor discrepancy between GPS and network location'}</p>
                      </div>
                    </div>
                  )}

                  {isLow && (
                    <div style={{ background: 'rgba(255,77,46,0.08)', border: '1px solid rgba(255,77,46,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>🔴</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 12, color: '#CC2200' }}>Significant discrepancy detected</p>
                        <p style={{ fontSize: 11, color: '#CC2200' }}>{record.locationTrustNote || 'GPS and network location do not match'}</p>
                      </div>
                    </div>
                  )}

                  {isUnverifiable && (
                    <div style={{ background: 'rgba(136,136,136,0.08)', border: '1px solid rgba(136,136,136,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>⚪</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 12, color: '#555' }}>Device was offline at capture</p>
                        <p style={{ fontSize: 11, color: '#666' }}>Location could not be cross-verified with network</p>
                      </div>
                    </div>
                  )}

                  {isHighWifi && (
                    <div style={{ background: 'rgba(0,200,138,0.08)', border: '1px solid rgba(0,200,138,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>📍</span>
                      <p style={{ fontWeight: 700, fontSize: 12, color: '#00C88A' }}>Precise location confirmed via WiFi</p>
                    </div>
                  )}

                  {isHighCell && (
                    <div style={{ background: 'rgba(0,200,138,0.08)', border: '1px solid rgba(0,200,138,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>📍</span>
                      <p style={{ fontWeight: 700, fontSize: 12, color: '#00C88A' }}>Location confirmed via cellular — approximate area shown</p>
                    </div>
                  )}

                  {/* HIGH + WiFi — precise pin, full opacity */}
                  {isHighWifi && (
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                      <iframe
                        title="Capture location"
                        width="100%" height="240"
                        style={{ border: 'none', display: 'block', opacity: 1 }}
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(record.longitude)-0.02},${parseFloat(record.latitude)-0.02},${parseFloat(record.longitude)+0.02},${parseFloat(record.latitude)+0.02}&layer=mapnik&marker=${record.latitude},${record.longitude}`}
                      />
                    </div>
                  )}

                  {/* HIGH + Cellular — approximate zone circle overlay */}
                  {isHighCell && (
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                      <iframe
                        title="Capture location approximate"
                        width="100%" height="240"
                        style={{ border: 'none', display: 'block', opacity: 0.9 }}
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(record.longitude)-0.06},${parseFloat(record.latitude)-0.06},${parseFloat(record.longitude)+0.06},${parseFloat(record.latitude)+0.06}&layer=mapnik&marker=${record.latitude},${record.longitude}`}
                      />
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'radial-gradient(circle at center, rgba(0,200,138,0.15) 25%, rgba(0,200,138,0.05) 55%, transparent 75%)',
                        pointerEvents: 'none',
                      }} />
                      <div style={{
                        position: 'absolute', bottom: 8, left: 8,
                        background: 'rgba(0,0,0,0.6)', borderRadius: 6,
                        padding: '3px 8px', fontSize: 10, color: 'white',
                      }}>⚠️ Approximate area — cellular accuracy</div>
                    </div>
                  )}

                  {/* MEDIUM — warning marker, faded map */}
                  {isMedium && (
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                      <iframe
                        title="Capture location unconfirmed"
                        width="100%" height="240"
                        style={{ border: 'none', display: 'block', opacity: 0.55, filter: 'saturate(0.5)' }}
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(record.longitude)-0.04},${parseFloat(record.latitude)-0.04},${parseFloat(record.longitude)+0.04},${parseFloat(record.latitude)+0.04}&layer=mapnik&marker=${record.latitude},${record.longitude}`}
                      />
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(200,168,75,0.12)', pointerEvents: 'none',
                      }} />
                      <div style={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(200,168,75,0.92)', borderRadius: 10,
                        padding: '8px 16px', textAlign: 'center',
                        border: '1px solid rgba(200,168,75,0.5)',
                      }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#5a3e00', margin: 0 }}>🟡 Location Approximate</p>
                        <p style={{ fontSize: 10, color: '#5a3e00', margin: '2px 0 0' }}>GPS and network location do not closely match</p>
                      </div>
                    </div>
                  )}

                  {/* LOW — hide map, show mismatch alert */}
                  {isLow && (
                    <div style={{
                      borderRadius: 10, border: '1px solid rgba(255,77,46,0.3)',
                      background: 'rgba(255,77,46,0.06)', padding: '20px 16px',
                      textAlign: 'center', marginBottom: 8,
                    }}>
                      <p style={{ fontSize: 24, margin: '0 0 8px' }}>🔴</p>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#CC2200', margin: '0 0 4px' }}>
                        Location Mismatch Detected
                      </p>
                      <p style={{ fontSize: 11, color: '#CC2200', margin: 0 }}>
                        Significant discrepancy between GPS and network location.<br/>
                        Map is hidden. GPS coordinates are still recorded.
                      </p>
                      <p style={{ fontSize: 10, color: '#aaa', marginTop: 8, fontFamily: 'monospace' }}>
                        {record.latitude}, {record.longitude}
                      </p>
                    </div>
                  )}

                  {/* UNVERIFIABLE — hide map, show offline notice */}
                  {isUnverifiable && (
                    <div style={{
                      borderRadius: 10, border: '1px solid rgba(136,136,136,0.25)',
                      background: 'rgba(136,136,136,0.06)', padding: '20px 16px',
                      textAlign: 'center', marginBottom: 8,
                    }}>
                      <p style={{ fontSize: 24, margin: '0 0 8px' }}>⚪</p>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#555', margin: '0 0 4px' }}>
                        No Network at Capture
                      </p>
                      <p style={{ fontSize: 11, color: '#666', margin: 0 }}>
                        Device was offline when this photo was taken.<br/>
                        Location could not be cross-verified with network data.
                      </p>
                      <p style={{ fontSize: 10, color: '#aaa', marginTop: 8, fontFamily: 'monospace' }}>
                        {record.latitude}, {record.longitude}
                      </p>
                    </div>
                  )}

                  {/* Coordinates — only shown when map is visible */}
                  {(isHighWifi || isHighCell) && (
                    <p style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                      {record.latitude}, {record.longitude}
                      {isHighCell && ' · Approximate area'}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* What this means */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>What does this mean?</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              'This photo was taken with the SafeSnap app at the exact time and location shown above.',
              'The SHA-256 fingerprint was generated at the moment of capture. Any edit to the photo — even one pixel — changes the fingerprint completely.',
              'This record is permanently stored in the SafeSnap cloud database and cannot be modified.',
            ].map((point, i) => (
              <div key={i} style={{ display: 'flex', gap: 10 }}>
                <span style={{ color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>{point}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Download app CTA */}
        <div style={{ background: 'var(--ink)', borderRadius: 16, padding: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ color: 'white', fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Protect yourself too</h3>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>Download SafeSnap and start taking verified photos for free.</p>
          </div>
          <a href="https://play.google.com/store/apps/details?id=com.safesnap.app" target="_blank" rel="noreferrer">
            <button className="btn-primary">Download free →</button>
          </a>
        </div>

      </div>

      <footer>
        <div className="footer-inner">
          <div className="footer-brand">SafeSnap</div>
          <div className="footer-copy">safesnap.app@gmail.com · Proof you can trust</div>
        </div>
      </footer>
    </>
  );
}
